import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Minus, Plus, Clock, Repeat, Sparkles } from 'lucide-react';
import { PlanService } from '../../services/PlanService';
import { BillingType, UserType } from '../../types/plan';
import { PlanDisplay } from './PlanCard';
import PlanDescriptionText from './PlanDescriptionText';
import {
  hubPlanCardShell,
  planAccentForDisplay,
  planAccentStyles,
} from './planCardStyles';

const DEFAULT_MIN_QUANTITY = 1;
const DEFAULT_MAX_QUANTITY = 50;

export type PlanSecondaryAction = {
  hint: string;
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
};

interface TherapistPlanCardProps {
  plan: PlanDisplay;
  onSelect: (id: string, quantity?: number) => void;
  isLoading: boolean;
  secondaryAction?: PlanSecondaryAction;
}

function secondaryButtonClass(accent: ReturnType<typeof planAccentForDisplay>) {
  if (accent === 'purple') {
    return 'border-[#2B5A41]/35 text-[#89A78C] hover:border-[#2B5A41]/55 hover:bg-[#2B5A41]/5 focus-visible:ring-[#2B5A41]';
  }
  if (accent === 'green') {
    return 'border-[#2B5A41]/35 text-[#2B5A41] hover:border-[#2B5A41]/55 hover:bg-[#2B5A41]/5 focus-visible:ring-[#2B5A41]';
  }
  return 'border-[#2B5A41]/35 text-[#2B5A41] hover:border-[#2B5A41]/55 hover:bg-[#2B5A41]/5 focus-visible:ring-[#2B5A41]';
}

const TherapistPlanCard: React.FC<TherapistPlanCardProps> = ({
  plan,
  onSelect,
  isLoading,
  secondaryAction,
}) => {
  const accent = planAccentForDisplay(plan);
  const styles = planAccentStyles[accent];
  const buttonText = PlanService.getButtonText(plan.isRecurring);

  const isCourse = plan.userType === UserType.COURSE;
  const supportsCustomQuantity = !isCourse && PlanService.supportsCustomQuantity(plan);

  const billingType = PlanService.normalizeBillingType(plan.billingType) ?? plan.billingType;
  const isFixedHoursPackage =
    !isCourse &&
    !supportsCustomQuantity &&
    billingType === BillingType.HOURS &&
    !plan.isRecurring &&
    (plan.quantity || 0) > 0 &&
    (plan.value || 0) > 0;

  const minQty = Number.isInteger(plan.minQuantity) ? (plan.minQuantity as number) : DEFAULT_MIN_QUANTITY;
  const maxQty = Number.isInteger(plan.maxQuantity) ? (plan.maxQuantity as number) : DEFAULT_MAX_QUANTITY;
  const pricePerUnit = supportsCustomQuantity ? (plan.value || 0) / (plan.quantity || 1) : 0;

  const [selectedQuantity, setSelectedQuantity] = useState<number>(() => Math.max(minQty, 1));

  const computedTotal = supportsCustomQuantity
    ? Math.round(pricePerUnit * selectedQuantity * 100) / 100
    : plan.value || 0;

  const decrement = () => setSelectedQuantity((q) => Math.max(minQty, q - 1));
  const increment = () => setSelectedQuantity((q) => Math.min(maxQty, q + 1));

  const periodLabel = (plan.period || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const planDescription = isFixedHoursPackage
    ? PlanService.stripHoursPricingFromDescription(plan.description || '')
    : plan.description;

  const PlanIcon = plan.isFeatured ? Sparkles : plan.isRecurring ? Repeat : Clock;

  return (
    <article className={hubPlanCardShell(styles.ring, plan.isFeatured)}>
      <div
        className={`pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br ${styles.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
        aria-hidden
      />

      <div className="relative flex flex-1 flex-col">
        {plan.isFeatured && (
          <span className="mb-4 inline-flex w-fit rounded-full bg-[#E0EDE3] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#2B5A41]">
            ⭐ Mais popular
          </span>
        )}

        <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl sm:h-14 sm:w-14 ${styles.iconBg}`}>
          <PlanIcon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden />
        </div>

        <h3 className="mb-2 text-lg font-bold leading-snug text-[#2B5A41] sm:text-xl">
          {plan.title}
        </h3>

        <div className="mb-4">
          {supportsCustomQuantity ? (
            <div>
              <p className="text-2xl font-bold text-[#2B5A41] sm:text-3xl">
                {PlanService.formatCurrency(computedTotal)}
              </p>
              <p className="mt-1 text-sm font-medium text-[#2B5A41]">
                {PlanService.formatHourRateLine(selectedQuantity, computedTotal)}
              </p>
            </div>
          ) : (
            <div>
              {plan.originalPrice && (
                <p className="text-sm text-[#2B5A41]/70 line-through">De {plan.originalPrice}</p>
              )}
              <p className="text-2xl font-bold text-[#2B5A41] sm:text-3xl">{plan.price}</p>
              {isFixedHoursPackage ? (
                <p className="mt-1 text-sm font-medium text-[#2B5A41]">
                  {PlanService.formatHourRateLine(plan.quantity || 1, plan.value || 0)}
                </p>
              ) : (
                periodLabel && (
                  <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-[#2B5A41]">
                    {periodLabel}
                  </p>
                )
              )}
            </div>
          )}
        </div>

        {supportsCustomQuantity && (
          <div className="mb-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#2B5A41]">
              Selecione a quantidade de horas ideal para você
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={decrement}
                disabled={selectedQuantity <= minQty || isLoading}
                aria-label="Diminuir quantidade"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#2B5A41]/25 bg-[#E0EDE3] text-[#2B5A41] transition hover:bg-[#2B5A41]/10 disabled:opacity-40"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-[2.5rem] text-center text-xl font-bold text-[#2B5A41]">
                {selectedQuantity}
              </span>
              <button
                type="button"
                onClick={increment}
                disabled={selectedQuantity >= maxQty || isLoading}
                aria-label="Aumentar quantidade"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#2B5A41]/25 bg-[#E0EDE3] text-[#2B5A41] transition hover:bg-[#2B5A41]/10 disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-center text-xs text-[#2B5A41]/70">
              Mínimo {minQty}h · Máximo {maxQty}h
            </p>
          </div>
        )}

        {(planDescription || (plan.features && plan.features.length > 0)) && (
          <div className="mb-4 flex flex-1 flex-col min-h-0">
            {planDescription && (
              <p className="mb-4 whitespace-pre-line text-sm leading-relaxed text-[#2B5A41]/90 sm:text-base">
                <PlanDescriptionText text={planDescription} />
              </p>
            )}

            {plan.features && plan.features.length > 0 && (
              <ul className="space-y-2 text-sm text-[#2B5A41]">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${styles.cta}`} strokeWidth={2.5} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-auto space-y-5">
          <button
            type="button"
            onClick={() => onSelect(plan.id || '', supportsCustomQuantity ? selectedQuantity : undefined)}
            disabled={isLoading}
            className={`w-full rounded-full px-6 py-3 text-sm font-semibold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${styles.button}`}
          >
            {isLoading ? 'Aguarde...' : buttonText}
          </button>

          {secondaryAction && (
            <div className="min-h-[8.75rem] border-t border-[#E0EDE3] pt-5">
              <p className="mb-3 text-center text-sm leading-relaxed text-[#5F7167]">
                {secondaryAction.hint}
              </p>
              {secondaryAction.href ? (
                <a
                  href={secondaryAction.href}
                  target={secondaryAction.external ? '_blank' : undefined}
                  rel={secondaryAction.external ? 'noopener noreferrer' : undefined}
                  className={`flex w-full items-center justify-center rounded-full border-2 bg-white px-6 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${secondaryButtonClass(accent)}`}
                >
                  {secondaryAction.label}
                </a>
              ) : (
                <Link
                  to={secondaryAction.to || '/'}
                  className={`flex w-full items-center justify-center rounded-full border-2 bg-white px-6 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${secondaryButtonClass(accent)}`}
                >
                  {secondaryAction.label}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default TherapistPlanCard;
